import { useEffect, useState, type MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { BottomNav } from "../components/BottomNav";
import { TransactionCard } from "../components/TransactionCard";
import { currentPeriod, formatDate, formatMoney, isMatchReady, previousPeriod } from "../lib/format";
import { useAuth } from "../store/auth";
import type {
  PendingReceiptOut,
  ReceiptUploadResponse,
  Transaction,
  TransactionListResponse,
} from "../types";

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState<TransactionListResponse | null>(null);
  const [pending, setPending] = useState<PendingReceiptOut[]>([]);
  const [insufficient, setInsufficient] = useState<Transaction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [overrulingId, setOverrulingId] = useState<number | null>(null);

  async function loadPending() {
    const list = await api.request<PendingReceiptOut[]>(
      "/receipts?pending_only=true&include_suggestions=true",
    );
    setPending(list);
  }

  async function overrulePending(item: PendingReceiptOut, e: MouseEvent) {
    e.stopPropagation();
    const txnId = item.receipt.already_matched_transaction_id;
    if (!txnId) return;
    if (
      !confirm(
        `This looks like the receipt already on transaction #${txnId}. Overrule that and match this copy to another card line? The existing match stays.`,
      )
    ) {
      return;
    }
    setOverrulingId(item.receipt.id);
    setError(null);
    try {
      const fresh = await api.request<ReceiptUploadResponse>(
        `/receipts/${item.receipt.id}/overrule`,
        { method: "POST" },
      );
      const raw = JSON.stringify(fresh);
      sessionStorage.setItem("lastUpload", raw);
      try {
        localStorage.setItem("lastUpload", raw);
      } catch {
        /* ignore */
      }
      navigate("/match");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not overrule");
      setOverrulingId(null);
    }
  }

  async function deletePending(receiptId: number, e: MouseEvent) {
    e.stopPropagation();
    if (!confirm("Delete this pending receipt?")) return;
    setDeletingId(receiptId);
    try {
      await api.request(`/receipts/${receiptId}`, { method: "DELETE" });
      setPending((prev) => prev.filter((p) => p.receipt.id !== receiptId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeletingId(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const periodQs = period === "all" ? "" : `period=${period}&`;
        const res = await api.request<TransactionListResponse>(
          `/transactions?${periodQs}status=unmatched&limit=100`,
        );
        const sug = await api.request<TransactionListResponse>(
          `/transactions?${periodQs}status=suggested&limit=100`,
        );
        const insuff = await api.request<TransactionListResponse>(
          `/transactions?${periodQs}status=insufficient&limit=100`,
        );
        const pend = await api.request<PendingReceiptOut[]>(
          "/receipts?pending_only=true&include_suggestions=true",
        );
        if (!cancelled) {
          setInsufficient(insuff.items);
          setData({
            items: [...sug.items, ...res.items],
            total: res.total + sug.total,
            unmatched: res.unmatched,
            matched: res.matched,
            suggested: sug.total,
          });
          setPending(pend);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const alreadyMatchedCount = pending.filter(
    (p) => p.receipt.already_matched_transaction_id,
  ).length;
  const readyCount = pending.filter(
    (p) =>
      !p.receipt.already_matched_transaction_id && isMatchReady(p.suggestions),
  ).length;
  const waitingCount = pending.length - readyCount - alreadyMatchedCount;

  function scrollToPending() {
    const el = document.getElementById("pending-receipts");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  useEffect(() => {
    if (loading || readyCount === 0) return;
    if (window.location.hash === "#pending-receipts") {
      // Let the section paint first
      requestAnimationFrame(() => scrollToPending());
    }
  }, [loading, readyCount]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-mark">
          <div className="logo">R</div>
          <div>
            <div className="name">Receipts</div>
            <div className="sub">{user?.full_name}</div>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" type="button" onClick={logout}>
          Log out
        </button>
      </header>

      {!loading && readyCount > 0 && (
        <button
          type="button"
          className="alert alert-ok"
          onClick={scrollToPending}
          style={{
            marginBottom: "1rem",
            width: "100%",
            textAlign: "left",
            cursor: "pointer",
            appearance: "none",
            font: "inherit",
          }}
        >
          <strong>
            {readyCount === 1
              ? "1 receipt ready to confirm"
              : `${readyCount} receipts ready to confirm`}
          </strong>
          <div style={{ marginTop: "0.25rem" }}>
            Card lines were imported that match your saved scans. Tap here to
            review them.
          </div>
        </button>
      )}

      <div className="stack" style={{ marginBottom: "1rem" }}>
        <div className="row between">
          <h2 style={{ fontSize: "1.35rem" }}>Open items</h2>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label="Period"
            style={{
              borderRadius: 999,
              border: "1px solid var(--line)",
              padding: "0.4rem 0.75rem",
              background: "#fff",
            }}
          >
            <option value="all">All open</option>
            <option value={previousPeriod()}>Last month</option>
            <option value={currentPeriod()}>This month</option>
          </select>
        </div>

        {data && (
          <div className="stat-grid">
            <div className="stat">
              <div className="n">{data.unmatched}</div>
              <div className="l">Open</div>
            </div>
            <div className="stat">
              <div className="n">{data.suggested}</div>
              <div className="l">Review</div>
            </div>
            <div className="stat">
              <div className="n">{pending.length}</div>
              <div className="l">Pending scans</div>
            </div>
          </div>
        )}
      </div>

      {error && <div className="alert">{error}</div>}
      {loading && <p className="muted">Loading…</p>}

      {!loading && insufficient.length > 0 && (
        <section className="stack" style={{ marginBottom: "1.25rem" }}>
          <div className="alert alert-warn" style={{ margin: 0 }}>
            <strong>
              {insufficient.length === 1
                ? "1 receipt has insufficient information"
                : `${insufficient.length} receipts have insufficient information`}
            </strong>
            <div style={{ marginTop: "0.25rem", fontSize: "0.85rem" }}>
              Add a better receipt, or explain what it was for and why there is no
              more information.
            </div>
          </div>
          {insufficient.map((txn) => (
            <div key={txn.id}>
              <TransactionCard
                txn={txn}
                to={`/transactions/${txn.id}`}
                state={{ siblingIds: insufficient.map((t) => t.id) }}
              />
              {txn.insufficient_reason && (
                <div className="muted" style={{ fontSize: "0.8rem", margin: "0.35rem 0.25rem 0" }}>
                  Admin: “{txn.insufficient_reason}”
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {!loading && pending.length > 0 && (
        <section
          id="pending-receipts"
          className="stack"
          style={{ marginBottom: "1.25rem", scrollMarginTop: "1rem" }}
        >
          <div className="row between">
            <h3 style={{ fontSize: "1.05rem", margin: 0 }}>Pending receipts</h3>
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              {readyCount} ready · {waitingCount} waiting on CSV
              {alreadyMatchedCount > 0 ? ` · ${alreadyMatchedCount} already matched` : ""}
            </span>
          </div>
          {pending.map((item) => {
            const { receipt, extraction, suggestions } = item;
            const alreadyTxn = receipt.already_matched_transaction_id ?? null;
            const ready = !alreadyTxn && isMatchReady(suggestions);
            const hasSuggestion = !alreadyTxn && suggestions.length > 0;
            return (
              <div key={receipt.id} className="txn-card" style={{ width: "100%" }}>
                <Link
                  to={`/receipts/${receipt.id}`}
                  state={{ siblingIds: pending.map((p) => p.receipt.id) }}
                  style={{
                    textAlign: "left",
                    width: "100%",
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <div className="row between">
                    <div className="merchant">
                      {extraction.merchant || receipt.extracted_merchant || "Receipt"}
                    </div>
                    <span
                      className={
                        alreadyTxn
                          ? "badge badge-matched"
                          : ready
                            ? "badge badge-suggested"
                            : "badge badge-unmatched"
                      }
                    >
                      {alreadyTxn
                        ? "Already matched"
                        : ready
                          ? "Match ready"
                          : hasSuggestion
                            ? "Possible match"
                            : "Waiting for CSV"}
                    </span>
                  </div>
                  <div className="row between" style={{ marginTop: "0.35rem" }}>
                    <span className="muted">
                      {extraction.date
                        ? formatDate(String(extraction.date))
                        : formatDate(receipt.created_at)}
                    </span>
                    <span className="amount">
                      {extraction.amount != null
                        ? formatMoney(String(extraction.amount), extraction.currency || "EUR")
                        : "—"}
                    </span>
                  </div>
                  <div className="muted" style={{ marginTop: "0.35rem", fontSize: "0.8rem" }}>
                    {(extraction.reference || receipt.extracted_reference) && (
                      <span className="mono">
                        Ref {extraction.reference || receipt.extracted_reference}
                        {" · "}
                      </span>
                    )}
                    {alreadyTxn
                      ? `Same receipt is already on transaction #${alreadyTxn}`
                      : ready
                        ? `Tap to view · ${suggestions.length} suggestion(s)`
                        : hasSuggestion
                          ? `Low/medium confidence · tap to review`
                          : "Tap to view receipt · waiting on statement"}
                  </div>
                </Link>
                <div className="row" style={{ marginTop: "0.55rem", justifyContent: "flex-end", gap: "0.5rem" }}>
                  {alreadyTxn && (
                    <button
                      type="button"
                      className="btn btn-accent btn-sm"
                      disabled={overrulingId === receipt.id || deletingId === receipt.id}
                      onClick={(e) => overrulePending(item, e)}
                    >
                      {overrulingId === receipt.id ? "Opening…" : "Overrule"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={deletingId === receipt.id || overrulingId === receipt.id}
                    onClick={(e) => deletePending(receipt.id, e)}
                  >
                    {deletingId === receipt.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => loadPending().catch(console.error)}
          >
            Refresh pending
          </button>
        </section>
      )}

      <div className="stack">
        <h3 style={{ fontSize: "1.05rem", margin: "0 0 0.35rem" }}>Card transactions</h3>
        {!loading && data?.items.length === 0 && (
          <div className="panel">
            <p style={{ margin: 0 }}>
              No open card lines for this period. You can still{" "}
              <Link to="/capture">scan a receipt</Link> before the CSV is imported —
              it will wait under Pending.
            </p>
          </div>
        )}
        {data?.items.map((txn, i) => (
          <div key={txn.id} style={{ animationDelay: `${i * 40}ms` }}>
            <TransactionCard
              txn={txn}
              to={`/transactions/${txn.id}`}
              state={{ siblingIds: data.items.map((t) => t.id) }}
            />
          </div>
        ))}
      </div>

      <BottomNav />
    </div>
  );
}
